import { TranslateFn } from 'app/hooks/useTranslations';
import {
  StateWorkOrder,
  WorkOrdersFilters,
  WorkOrderType,
} from 'app/interfaces/workOrder';
import { mapQueryParamsToFilters } from 'app/utils/utilsWorkOrder';
import {
  Column,
  ColumnFormat,
} from 'components/table/interface/interfaceTable';
import dayjs from 'dayjs';

const creationDateColumn = (t: TranslateFn): Column => ({
  label: t('workorder.creation.date'),
  key: 'creationTime',
  format: ColumnFormat.DATETIME,
});

const startDateColumn = (t: TranslateFn): Column => ({
  label: t('workorder.start.date'),
  key: 'startTime',
  format: ColumnFormat.WORKORDERSTARTTIME,
});

const getCommonColumns = (t: any, dateColumn: Column): Column[] => [
  {
    label: t('common.id'),
    key: 'id',
    format: ColumnFormat.TEXT,
  },
  {
    label: t('workorder.serial.number'),
    key: 'code',
    format: ColumnFormat.WORKORDERCODE,
  },
  {
    label: t('common.description'),
    key: 'description',
    format: ColumnFormat.TEXT,
  },
  {
    label: t('workorder.type'),
    key: 'workOrderType',
    format: ColumnFormat.WORKORDERTYPE,
  },
  dateColumn,
  {
    label: t('due.date'),
    key: 'dueDate',
    format: ColumnFormat.DATE,
  },
  {
    label: t('workorder.state'),
    key: 'stateWorkOrder',
    format: ColumnFormat.STATEWORKORDER,
  },
];

export const getBaseColumns = (t: any): Column[] => [
  ...getCommonColumns(t, startDateColumn(t)),
  {
    label: t('workorder.equipment'),
    key: 'assetDescription',
    format: ColumnFormat.TEXT,
  },
  {
    label: t('brand'),
    key: 'assetBrand',
    format: ColumnFormat.TEXT,
  },
  {
    label: t('workorder.relatedWorkOrder'),
    key: 'relatedWorkOrder',
    format: ColumnFormat.RELATEDWORKORDER,
  },
  {
    label: t('workorder.operators'),
    key: 'operatorsNames',
    format: ColumnFormat.TEXT,
    width: 'w-1/4',
  },
  {
    label: t('system.qualityValidation'),
    key: 'qualityStatus',
    format: ColumnFormat.QUALITYSTATUS,
    defaultHidden: true,
  },
  { ...creationDateColumn(t), defaultHidden: true },
];

export const getColumnsTicket = (t: any): Column[] => [
  ...getCommonColumns(t, creationDateColumn(t)),
  startDateColumn(t),
  {
    label: t('workorder.equipment'),
    key: 'assetDescription',
    format: ColumnFormat.TEXT,
  },
  {
    label: t('workorder.relatedWorkOrder'),
    key: 'relatedWorkOrder',
    format: ColumnFormat.RELATEDWORKORDER,
  },
];

export const getColumnsCRM = (t: any): Column[] => [
  ...getCommonColumns(t, creationDateColumn(t)),
  startDateColumn(t),
  {
    label: t('customer.customer'),
    key: 'customerName',
    format: ColumnFormat.TEXT,
  },
  {
    label: t('customer.store'),
    key: 'customerInstallationCity',
    format: ColumnFormat.TEXT,
  },
  {
    label: t('common.code'),
    key: 'customerInstallationCode',
    format: ColumnFormat.TEXT,
  },
  {
    label: t('customer.reference'),
    key: 'refCustomerId',
    format: ColumnFormat.TEXT,
  },
  {
    label: t('workorder.operators'),
    key: 'operatorsNames',
    format: ColumnFormat.TEXT,
    width: 'w-1/4',
  },
];

const MAX_TEXT_LENGTH = 200;
const MAX_DATE_LENGTH = 40;
const MIN_VALID_YEAR = 1900;
const MAX_VALID_YEAR = 2200;
const STATE_VALUES: number[] = Object.values(StateWorkOrder).filter(
  (value): value is number => typeof value === 'number'
);
const TYPE_VALUES: number[] = Object.values(WorkOrderType).filter(
  (value): value is number => typeof value === 'number'
);

export interface WorkOrdersFiltersPatch {
  workOrderType?: WorkOrderType[];
  workOrderState?: StateWorkOrder[];
  startDate?: Date;
  endDate?: Date;
  searchTerm?: string;
  assetId?: string;
  refCustomerId?: string;
  customerName?: string;
  isInvoiced?: boolean;
  hasDeliveryNote?: boolean;
  hasCorrectiveCreated?: boolean | null;
  onlyUnassigned?: boolean;
  pendingQuality?: boolean;
  qualityValidated?: boolean;
}

export const getDefaultWorkOrdersFilters = (
  isCRM: boolean,
  operatorLogged: boolean
): WorkOrdersFilters =>
  mapQueryParamsToFilters({}, isCRM, operatorLogged, undefined);

const sameNumbers = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const sameDay = (a: Date | null, b: Date | null): boolean =>
  a === null || b === null
    ? a === b
    : dayjs(a).format('YYYY-MM-DD') === dayjs(b).format('YYYY-MM-DD');

const isValidDate = (date: Date): boolean => {
  const year = date.getFullYear();
  return (
    !isNaN(date.getTime()) && year >= MIN_VALID_YEAR && year <= MAX_VALID_YEAR
  );
};

export const serializeWorkOrdersFilters = (
  filters: WorkOrdersFilters,
  defaults: WorkOrdersFilters = getDefaultWorkOrdersFilters(false, false)
): Record<string, unknown> => {
  const state: Record<string, unknown> = {};

  if (!sameNumbers(filters.workOrderType, defaults.workOrderType)) {
    state.workOrderType = [...filters.workOrderType];
  }
  if (!sameNumbers(filters.workOrderState, defaults.workOrderState)) {
    state.workOrderState = [...filters.workOrderState];
  }
  const { startDate, endDate } = filters.dateRange;
  if (startDate && !sameDay(startDate, defaults.dateRange.startDate)) {
    state.startDate = startDate.toISOString();
  }
  if (endDate && !sameDay(endDate, defaults.dateRange.endDate)) {
    state.endDate = endDate.toISOString();
  }
  if (filters.searchTerm !== defaults.searchTerm) {
    state.searchTerm = filters.searchTerm;
  }
  if (filters.assetId !== defaults.assetId) state.assetId = filters.assetId;
  if (filters.refCustomerId !== defaults.refCustomerId) {
    state.refCustomerId = filters.refCustomerId;
  }
  if (filters.customerName !== defaults.customerName) {
    state.customerName = filters.customerName;
  }
  if (filters.isInvoiced !== defaults.isInvoiced) {
    state.isInvoiced = filters.isInvoiced;
  }
  if (filters.hasDeliveryNote !== defaults.hasDeliveryNote) {
    state.hasDeliveryNote = filters.hasDeliveryNote;
  }
  if (filters.hasCorrectiveCreated !== defaults.hasCorrectiveCreated) {
    state.hasCorrectiveCreated = filters.hasCorrectiveCreated;
  }
  if (filters.onlyUnassigned !== defaults.onlyUnassigned) {
    state.onlyUnassigned = filters.onlyUnassigned;
  }
  if (filters.pendingQuality !== defaults.pendingQuality) {
    state.pendingQuality = filters.pendingQuality;
  }
  if (filters.qualityValidated !== defaults.qualityValidated) {
    state.qualityValidated = filters.qualityValidated;
  }

  return state;
};

const parseEnumList = (
  value: unknown,
  allowed: number[]
): number[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const valid = value.every(
    item => typeof item === 'number' && allowed.includes(item)
  );
  return valid ? Array.from(new Set<number>(value)) : undefined;
};

const parseText = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length <= MAX_TEXT_LENGTH
    ? value
    : undefined;

const parseDate = (value: unknown): Date | undefined => {
  if (typeof value !== 'string' || value.length > MAX_DATE_LENGTH) {
    return undefined;
  }
  const date = new Date(value);
  return isValidDate(date) ? date : undefined;
};

const parseBoolean = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

export const parseWorkOrdersFilters = (
  state: Record<string, unknown>
): WorkOrdersFiltersPatch => {
  const patch: WorkOrdersFiltersPatch = {};

  const types = parseEnumList(state.workOrderType, TYPE_VALUES);
  if (types) patch.workOrderType = types;
  const states = parseEnumList(state.workOrderState, STATE_VALUES);
  if (states) patch.workOrderState = states;

  const startDate = parseDate(state.startDate);
  const endDate = parseDate(state.endDate);
  const invertedRange =
    startDate !== undefined &&
    endDate !== undefined &&
    startDate.getTime() > endDate.getTime();
  if (!invertedRange) {
    if (startDate) patch.startDate = startDate;
    if (endDate) patch.endDate = endDate;
  }

  const searchTerm = parseText(state.searchTerm);
  if (searchTerm !== undefined) patch.searchTerm = searchTerm;
  const assetId = parseText(state.assetId);
  if (assetId !== undefined) patch.assetId = assetId;
  const refCustomerId = parseText(state.refCustomerId);
  if (refCustomerId !== undefined) patch.refCustomerId = refCustomerId;
  const customerName = parseText(state.customerName);
  if (customerName !== undefined) patch.customerName = customerName;

  const isInvoiced = parseBoolean(state.isInvoiced);
  if (isInvoiced !== undefined) patch.isInvoiced = isInvoiced;
  const hasDeliveryNote = parseBoolean(state.hasDeliveryNote);
  if (hasDeliveryNote !== undefined) patch.hasDeliveryNote = hasDeliveryNote;
  const onlyUnassigned = parseBoolean(state.onlyUnassigned);
  if (onlyUnassigned !== undefined) patch.onlyUnassigned = onlyUnassigned;
  const pendingQuality = parseBoolean(state.pendingQuality);
  if (pendingQuality !== undefined) patch.pendingQuality = pendingQuality;
  const qualityValidated = parseBoolean(state.qualityValidated);
  if (qualityValidated !== undefined) patch.qualityValidated = qualityValidated;
  if (state.hasCorrectiveCreated === null) {
    patch.hasCorrectiveCreated = null;
  } else {
    const corrective = parseBoolean(state.hasCorrectiveCreated);
    if (corrective !== undefined) patch.hasCorrectiveCreated = corrective;
  }

  return patch;
};

export const resolveWorkOrdersFilters = (
  state: Record<string, unknown>,
  defaults: WorkOrdersFilters,
  current?: WorkOrdersFilters
): WorkOrdersFilters => {
  const { startDate, endDate, ...patch } = parseWorkOrdersFilters(state);
  return {
    ...defaults,
    ...patch,
    dateRange: {
      startDate: startDate ?? defaults.dateRange.startDate,
      endDate: endDate ?? defaults.dateRange.endDate,
    },
    active: current?.active ?? defaults.active,
    useOperatorLogged: current?.useOperatorLogged ?? defaults.useOperatorLogged,
  };
};
